import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Store } from 'lucide-react';

import RestaurantImg from '../../assets/App_Img/partner_cat_restaurant.png';
import ActivityImg from '../../assets/App_Img/partner_cat_activity.png';
import ShopImg from '../../assets/App_Img/partner_cat_shop.png';
import landingData from '../landingData.json';

const categoryImages = [RestaurantImg, ActivityImg, ShopImg];

const CategoriesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const tabs = landingData.categoriesData.map((item, idx) => ({
    ...item,
    img: categoryImages[idx % categoryImages.length]
  }));

  // Auto-play Carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % tabs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeIndex, tabs.length]);

  const handleNext = () => setActiveIndex((prev) => (prev + 1) % tabs.length);
  const handlePrev = () => setActiveIndex((prev) => (prev - 1 + tabs.length) % tabs.length);

  // Function to calculate exact positioning for the peeking carousel effect
  const getCardAnimation = (idx) => {
    let offset = idx - activeIndex;
    if (offset < -1) offset += tabs.length;
    if (offset > 1) offset -= tabs.length;

    if (offset === 0) {
      // Active Center Card
      return { x: "0%", scale: 1, zIndex: 30, opacity: 1, filter: "blur(0px)" };
    } else if (offset === 1) {
      // Right Side
      return { x: "110%", scale: 1, zIndex: 20, opacity: 1, filter: "blur(0px)" };
    } else if (offset === -1) {
      // Left Side
      return { x: "-110%", scale: 1, zIndex: 20, opacity: 1, filter: "blur(0px)" };
    } else {
      return { x: "220%", scale: 1, zIndex: 10, opacity: 0, filter: "blur(0px)" };
    }
  };

  return (
    <section className="py-24 bg-white relative overflow-hidden border-t border-b border-gray-100" id="categories">
      <div className="w-full space-y-10">

        {/* Header */}
        <div className="text-center max-w-4xl mx-auto px-4">
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11.5px] font-bold tracking-wider uppercase px-4 py-1.5 rounded-full text-[#044E4D] bg-[#E0F2F1] border border-[#067978]/25">
              <Store size={14} className="text-[#067978]" /> Who joins Trrip
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a2b49] tracking-tight">
            The businesses <span className="text-[#067978]">travellers are looking for.</span>
          </h2>
          <p className="text-gray-500 font-normal mt-4 text-[15px] max-w-xl mx-auto">
            From family cafés to jet-ski operators to artisan shops — if travellers seek you out, you belong here.
          </p>
        </div>

        {/* Full-Width Peeking Carousel */}
        <div className="relative w-full h-[480px] sm:h-[420px] md:h-[500px] flex items-center justify-center overflow-hidden px-2 sm:px-4 md:px-0">

          {tabs.map((tab, idx) => {
            const isCenter = idx === activeIndex;
            return (
              <motion.div
                key={idx}
                animate={getCardAnimation(idx)}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className={`absolute w-[92vw] sm:w-[82vw] md:w-[65vw] max-w-5xl h-[450px] sm:h-[400px] md:h-[460px] bg-[#f8fafc] rounded-[1.8rem] sm:rounded-[2.5rem] flex flex-col md:flex-row overflow-hidden cursor-pointer group transition-all duration-500 ${isCenter
                    ? 'border-t-[6px] border-t-[#067978] shadow-[0_20px_60px_rgba(6,121,120,0.15)]'
                    : 'border border-gray-100 shadow-[0_10px_40px_rgba(0,0,0,0.06)]'
                  }`}
                onClick={() => setActiveIndex(idx)}
              >
                {/* Card Content - Left Side */}
                <div className="w-full md:w-1/2 p-5 sm:p-8 md:p-14 flex flex-col justify-center space-y-2.5 sm:space-y-4 relative z-40">
                  <span className="text-[10px] sm:text-xs font-extrabold text-[#067978] tracking-widest uppercase">
                    {tab.num} Partner Category
                  </span>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-[#1a2b49] leading-tight transition-colors duration-300 group-hover:text-[#067978]">
                    {tab.title}
                  </h3>
                  <p className="text-gray-500 font-normal text-xs sm:text-sm md:text-base leading-relaxed max-w-sm">
                    {tab.desc}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 pt-1 sm:pt-2">
                    {tab.tags.map((tag, tIdx) => (
                      <span
                        key={tIdx}
                        className="text-[10px] sm:text-[11px] font-bold px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full bg-white border border-[#067978]/20 text-[#044E4D] shadow-2xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Card Image - Right Side (Hyper-Specific 3D Category Artwork) */}
                <div className="w-full md:w-1/2 h-[180px] sm:h-[200px] md:h-full flex items-center justify-center p-3 sm:p-6 md:p-10 relative z-30 overflow-hidden">
                  <img
                    src={tab.img}
                    alt={tab.title}
                    className="max-w-[85%] sm:max-w-[90%] max-h-[85%] sm:max-h-[90%] object-contain mix-blend-multiply transition-transform duration-700 ease-out group-hover:scale-105 group-hover:-translate-y-2"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Carousel Controls */}
        <div className="flex items-center justify-center pt-2">
          <div className="flex items-center gap-4 bg-white px-5 py-2.5 rounded-full border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <button
              onClick={handlePrev}
              className="p-1 text-gray-400 hover:text-[#067978] transition-colors border-none bg-transparent cursor-pointer"
              aria-label="Previous slide"
            >
              <ChevronLeft size={22} strokeWidth={3} />
            </button>

            {/* Dots */}
            <div className="flex gap-2.5">
              {tabs.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`h-2.5 rounded-full transition-all duration-300 border-none cursor-pointer ${activeIndex === idx ? 'w-8 bg-[#067978]' : 'w-2.5 bg-gray-200 hover:bg-gray-300'
                    }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="p-1 text-gray-400 hover:text-[#067978] transition-colors border-none bg-transparent cursor-pointer"
              aria-label="Next slide"
            >
              <ChevronRight size={22} strokeWidth={3} />
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};

export default CategoriesSection;
